// biome-ignore-all lint/suspicious/noArrayIndexKey: highlightText splits a string into a fixed, order-stable list of text/mark fragments; the index is the natural key (same rationale as ResourceBody).
// =============================================================================
// ResourceSearchModal.jsx — command-palette search over the Resources wiki
// =============================================================================
// Opens from the magnifier in the Resources header (or Ctrl/Cmd+K, "/"). Loads
// every section's pages once, keyword-filters client-side, and on select calls
// onOpen(section, pageId, query) so the caller can navigate + highlight.
// See docs/resources-search-plan.md.
// =============================================================================

import React from "react";
import { listAllResourcePages } from "../../data/resourcePages";
import { searchResources } from "../../services/resourceSearch";
import { ancestorTitles } from "../../services/resourceTree";
import { ModalBackdrop } from "../form/FormUI.jsx";

const SECTION_LABELS = { general: "General", invoicing: "Invoicing", responders: "Responders" };

// Wrap the query terms in a plain-text string with <mark> (for display only).
function highlightText(text, query) {
  const terms = (query || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!text || terms.length === 0) return text;
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  return String(text)
    .split(re)
    .map(function (part, i) {
      return terms.includes(part.toLowerCase()) ? (
        <mark key={`m-${i}`} style={{ backgroundColor: "#FDE68A", color: "inherit" }}>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`t-${i}`}>{part}</React.Fragment>
      );
    });
}

export function ResourceSearchModal({ open, onClose, currentSection, onOpen }) {
  const [allPages, setAllPages] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [thisSectionOnly, setThisSectionOnly] = React.useState(false);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const inputRef = React.useRef(null);

  // Load every section's pages once, the first time the modal opens.
  React.useEffect(
    function () {
      if (!open || allPages) return;
      let live = true;
      listAllResourcePages().then(function (r) {
        if (live) setAllPages(r || []);
      });
      return function () {
        live = false;
      };
    },
    [open, allPages]
  );

  // Focus the input each time it opens.
  React.useEffect(
    function () {
      if (!open) return;
      setActiveIdx(0);
      const t = setTimeout(function () {
        if (inputRef.current) inputRef.current.focus();
      }, 0);
      return function () {
        clearTimeout(t);
      };
    },
    [open]
  );

  const pool = React.useMemo(
    function () {
      const base = allPages || [];
      return thisSectionOnly ? base.filter((p) => p.section === currentSection) : base;
    },
    [allPages, thisSectionOnly, currentSection]
  );
  const results = React.useMemo(() => searchResources(pool, q), [pool, q]);
  React.useEffect(
    function () {
      setActiveIdx(0);
    },
    [q, thisSectionOnly]
  );

  if (!open) return null;

  function openResult(res) {
    if (res) onOpen(res.page.section, res.page.id, q);
  }

  // Arrow/Enter on the input drive the result list (Esc is handled by ModalBackdrop).
  function onKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      openResult(results[activeIdx]);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        style={{
          background: "white",
          borderRadius: 12,
          width: "min(640px, 92vw)",
          overflow: "hidden",
        }}
      >
        <div className="flex items-center gap-3 border-b border-zephyr-border px-4 py-3">
          <span aria-hidden="true" className="text-zephyr-muted text-[15px]">
            🔍
          </span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search Resources…"
            aria-label="Search Resources"
            className="flex-1 outline-none text-[15px] text-zephyr-text bg-transparent"
          />
          <label className="flex items-center gap-1 text-[12px] text-zephyr-muted whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={thisSectionOnly}
              onChange={(e) => setThisSectionOnly(e.target.checked)}
            />
            This section only
          </label>
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {allPages === null ? (
            <div className="p-5 text-zephyr-muted text-[13px]">Loading…</div>
          ) : q.trim() === "" ? (
            <div className="p-5 text-zephyr-muted text-[13px]">
              Type a keyword to search {thisSectionOnly ? "this section" : "all Resources"}.
            </div>
          ) : results.length === 0 ? (
            <div className="p-5 text-zephyr-muted text-[13px]">No pages match “{q}”.</div>
          ) : (
            results.map(function (res, i) {
              const crumb = [
                SECTION_LABELS[res.page.section] || res.page.section,
                ...ancestorTitles(allPages || [], res.page.id),
              ].join(" › ");
              const active = i === activeIdx;
              return (
                <button
                  key={res.page.id}
                  type="button"
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => openResult(res)}
                  className={`block w-full text-left px-4 py-2.5 border-b border-zephyr-border cursor-pointer ${
                    active ? "bg-zephyr-panel-bg" : "bg-white"
                  }`}
                >
                  <div className="text-[11px] text-zephyr-muted">{crumb}</div>
                  <div className="text-[14px] text-zephyr-text font-semibold">
                    {highlightText(res.page.title, q)}
                  </div>
                  {res.snippet ? (
                    <div className="text-[12px] text-zephyr-muted mt-[2px]">
                      {highlightText(res.snippet, q)}
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
